/**
 * Client-generated idempotency key — one per buy intent (§10 Idempotency).
 * Reused verbatim on every retry of the same intent (double-tap, timeout,
 * "try again") so the backend replays the original purchase instead of
 * charging twice. Uses the Web Crypto UUID with a getRandomValues fallback
 * for older WebViews.
 */
export const generateIdempotencyKey = (): string => {
	if (
		typeof crypto !== 'undefined' &&
		typeof crypto.randomUUID === 'function'
	) {
		return crypto.randomUUID();
	}

	// RFC 4122 v4 fallback.
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
	return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
		.slice(6, 8)
		.join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
};

/**
 * Operator-specific legal info shown in the AGB acceptance block. The links are
 * configured per operator in the backend (GET /api/v1/operators/{id}); the
 * legal name is the operator's display name.
 */
export interface IOperatorLegalInfo {
	legalName: string;
	agbUrl: string;
	cancellationUrl: string;
}

export const getOperatorLegalInfo = (
	operatorName: string | undefined,
	agbUrl: string | undefined,
	cancellationUrl: string | undefined
): IOperatorLegalInfo => ({
	legalName: operatorName ?? '',
	agbUrl: agbUrl ?? '',
	cancellationUrl: cancellationUrl ?? '',
});
